import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { CheckCircle, TrendingUp } from '@mui/icons-material';
import AIConfidenceBadge from '../AIConfidenceBadge';
import {
  formatVolume,
  formatRiskPct,
  cellDense,
  stickyLeft,
  stickyLayer,
  stickyBodyBg,
  STICKY_HEADER_Z,
  STICKY_BODY_Z,
  STICKY_BG_HOVER,
} from './utils';

const thSx = {
  py: 0.75,
  fontWeight: 700,
  fontSize: '0.65rem',
  letterSpacing: '0.04em',
  color: '#064e3b',
  textTransform: 'uppercase',
  backgroundColor: '#ecfdf5',
};

const BuyReadyTable = ({ results }) => {
  const { t, i18n } = useTranslation();

  const buyReady = (results?.results || []).filter(
    (r) => r.trigger_flag || r.early_trigger_flag
  );

  if (buyReady.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        mb: 3,
        overflow: 'hidden',
        border: '2px solid #86efac',
        background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          borderBottom: '1px solid #a7f3d0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircle sx={{ fontSize: 16, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: '#065f46', fontSize: '0.95rem', lineHeight: 1.2 }}>
              {t('earlyPivot.buyReady.title', { defaultValue: 'Sẵn sàng mua' })}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#059669', fontWeight: 500 }}>
              {t('earlyPivot.buyReady.subtitle', { defaultValue: 'Đã xác nhận trigger — có thể vào lệnh' })}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${buyReady.length} ${t('earlyPivot.buyReady.stocks', { defaultValue: 'cổ phiếu' })}`}
          size="small"
          sx={{
            bgcolor: '#10b981',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.75rem',
            height: 26,
          }}
        />
      </Box>

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyLeft(0),
                  ...stickyLayer,
                  zIndex: STICKY_HEADER_Z,
                  width: 36, minWidth: 36, maxWidth: 36,
                  ...thSx,
                  borderRight: '1px solid #a7f3d0',
                }}
              >
                #
              </TableCell>
              <TableCell
                sx={{
                  ...stickyLeft(36),
                  ...stickyLayer,
                  zIndex: STICKY_HEADER_Z,
                  width: 72, minWidth: 72,
                  ...thSx,
                  borderRight: '1px solid #a7f3d0',
                }}
              >
                TICKER
              </TableCell>
              <TableCell align="center" sx={{ ...thSx, width: 80 }}>
                {t('earlyPivot.tableMain.colTriggerDate', { defaultValue: 'NGÀY TRIGGER' })}
              </TableCell>
              <TableCell sx={{ ...thSx, width: 120 }}>
                {t('earlyPivot.tableMain.colItems', { defaultValue: 'ITEMS' })}
              </TableCell>
              <TableCell align="center" sx={{ ...thSx, width: 90 }}>
                % SETUP
              </TableCell>
              <TableCell align="center" sx={{ ...thSx, minWidth: 96 }}>
                {t('earlyPivot.tableMain.colAi', { defaultValue: 'AI' })}
              </TableCell>
              <TableCell sx={{ ...thSx, minWidth: 140 }}>
                {t('earlyPivot.tableMain.colCompany', { defaultValue: 'CÔNG TY' })}
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 72 }}>
                CLOSE
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 68 }}>
                VOLUME
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 64 }}>
                STOP
              </TableCell>
              <TableCell align="right" sx={{ ...thSx, width: 64 }}>
                RISK
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buyReady.map((row, i) => (
              <TableRow
                key={row.ticker}
                className="early-pivot-row"
                sx={{
                  bgcolor: 'rgba(16, 185, 129, 0.03)',
                  borderBottom: '1px solid #d1fae5',
                  transition: 'all 0.12s',
                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.07)' },
                  '&:hover .ep-sticky': { backgroundColor: STICKY_BG_HOVER },
                }}
              >
                <TableCell
                  className="ep-sticky"
                  sx={{
                    ...cellDense,
                    ...stickyLeft(0),
                    ...stickyLayer,
                    zIndex: STICKY_BODY_Z,
                    backgroundColor: stickyBodyBg(true),
                    color: '#065f46',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    width: 36, minWidth: 36,
                    borderRight: '1px solid #d1fae5',
                  }}
                >
                  {i + 1}
                </TableCell>
                <TableCell
                  className="ep-sticky"
                  sx={{
                    ...cellDense,
                    ...stickyLeft(36),
                    ...stickyLayer,
                    zIndex: STICKY_BODY_Z,
                    backgroundColor: stickyBodyBg(true),
                    width: 72, minWidth: 72,
                    borderRight: '1px solid #d1fae5',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: '#065f46', fontSize: '0.88rem', letterSpacing: '0.02em' }}>
                    {row.ticker}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={cellDense}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#059669', fontVariantNumeric: 'tabular-nums' }}>
                    {row.trigger_date ? new Date(row.trigger_date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={cellDense}>
                  {row.trigger_type ? (
                    <Chip
                      size="small"
                      icon={<TrendingUp sx={{ fontSize: 12 }} />}
                      label={row.trigger_type
                        .replace('Pocket Pivot', 'POCKET_PIVOT')
                        .replace('Near Resistance Squeeze', 'NEAR_RES_SQUEEZE')
                        .replace('Spring', 'SPRING')
                        .replace('Breakout', 'BREAKOUT')
                        .replace('Setup Only', 'SETUP_ONLY')
                        .toUpperCase()}
                      sx={{
                        height: 22,
                        bgcolor: '#d1fae5',
                        color: '#065f46',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        fontFamily: 'monospace',
                        '& .MuiChip-icon': { color: '#10b981' },
                      }}
                    />
                  ) : '—'}
                </TableCell>
                <TableCell align="center" sx={cellDense}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 32, height: 5, bgcolor: '#a7f3d0', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ width: `${row.setup_score}%`, height: '100%', bgcolor: row.setup_score >= 90 ? '#059669' : '#10b981' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669', fontVariantNumeric: 'tabular-nums' }}>
                      {row.setup_score}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center" sx={cellDense}>
                  <AIConfidenceBadge
                    confidence={row.ai_confidence}
                    language={i18n.language}
                    tooltipTitle={t(
                      'earlyPivot.tableMain.aiConfTooltip',
                      'AI confidence \u2014 pattern reliability win probability',
                    )}
                    compact
                  />
                </TableCell>
                <TableCell sx={cellDense}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#334155', lineHeight: 1.3 }}>{row.companyName || '—'}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                  {row.close != null ? Number(row.close).toLocaleString('vi-VN') : '—'}
                </TableCell>
                <TableCell align="right" sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', fontSize: '0.72rem', color: '#64748b' }}>
                  {formatVolume(row.volume)}
                </TableCell>
                <TableCell align="right" sx={{ ...cellDense, fontVariantNumeric: 'tabular-nums', fontSize: '0.72rem', color: '#64748b' }}>
                  {row.stop != null ? Number(row.stop).toLocaleString('vi-VN') : '—'}
                </TableCell>
                <TableCell align="right" sx={cellDense}>
                  {(() => {
                    const v = row.risk_pct != null ? Number(row.risk_pct) * 100 : null;
                    const color = v != null ? (v < 5 ? '#059669' : v < 10 ? '#ca8a04' : '#dc2626') : '#64748b';
                    return (
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>
                        {formatRiskPct(row.risk_pct)}
                      </Typography>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BuyReadyTable;
